import EmberService, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type DSModel from '@ember-data/model';

import type { APIData, CreateRecordProperties, ImageCropData, Service } from 'webapp-types';
import {
	BatchSelectActionType,
	BatchSelectObjectType,
	ModelName,
	PopupEvent,
} from 'webapp/enums/enums';
import { capitalize, uniq } from 'webapp/utils/common';

import type BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';

type updateModelCacheHelperCreateFn<T1, T2> = (record: T1) => T2;
export interface BatchSelectActionOptions {
	hideInfolayer?: boolean;
	// For a batch on another object type than this service's model (e.g. Metrics page registry rows).
	objectType?: BatchSelectObjectType;
	objectText?: string;
}

// the visual settings a new board / report slide takes from the account's global ones (when not given)
interface GlobalVisualSettingsProperties {
	color?: string;
	themeId?: number;
	bg?: string;
	bgOpacity?: number;
	bgOriginal?: string;
	bgCrop?: ImageCropData | null;
	logo?: string;
	logoOriginal?: string;
	logoCrop?: ImageCropData | null;
}

export default abstract class ModelManagerForStoreService<
	RecordType extends DSModel,
	ModelType extends BufferedModel<DSModel, object>, // its record can be a union with RecordType (e.g. board | board-preview)
> extends EmberService {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;

	abstract modelName: string; // store record type
	modelTitle?: string;

	// ------ FETCHING RECORDS & MODELS ------

	get allRecords(): RecordType[] {
		const records = this.storeService.peekAll(this.modelName).slice();
		// slice (same as legacy toArray) forces autotracking for some reason. More info https://app.asana.com/0/0/1206115486733872/f
		return records as RecordType[];
	}

	@cached
	get savedRecords(): RecordType[] {
		return this.allRecords.filter((record: RecordType) => !!record.id && !record.isDeleted);
	}

	findRecord(id: string): RecordType | null {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		this.allRecords; // add to autotracking
		return this.findRecordQuick(id);
	}

	findRecordQuick(id: string): RecordType | null {
		// more performant, but not for use in getters / autotracked content
		if (id) {
			return (this.storeService.peekRecord(this.modelName, id) as RecordType) || null;
		} else {
			return null;
		}
	}

	findModel(id: string): ModelType | null {
		let model;
		const record = this.findRecord(id);
		if (record) {
			model = this.createModelFromRecord(record);
		}
		return model || null;
	}

	findModelQuick(id: string): ModelType | null {
		// more performant, but not for use in getters / autotracked content
		let model;
		const record = this.findRecordQuick(id);
		if (record) {
			model = this.createModelFromRecord(record);
		}
		return model || null;
	}

	// ------ CREATING RECORDS & MODELS ------

	abstract createModel(_model: RecordType): ModelType;

	createNewRecord(properties: CreateRecordProperties<RecordType> = {}): RecordType {
		return this.storeService.createRecord(
			this.modelName,
			properties as Record<string, unknown>,
		) as RecordType;
	}

	createNewModel(properties: CreateRecordProperties<RecordType> = {}): ModelType {
		const model = this.createNewRecord(properties);
		return this.createModelFromRecord(model);
	}

	createModelFromRecord(model: RecordType): ModelType {
		return this.createModel(model);
	}

	createModelsFromRecordsArray(recordsArray: RecordType[]): ModelType[] {
		return recordsArray.map((model: RecordType) => {
			return this.createModelFromRecord(model);
		});
	}

	createModelCopy(model: ModelType): ModelType {
		return this.createNewModel(model.getDataForCopy());
	}

	// ------ BATCH ACTIONS ------

	get objectType(): BatchSelectObjectType {
		const normalizedModelName = this.modelName
			.replace('-', '_')
			.toUpperCase() as keyof typeof BatchSelectObjectType;
		return BatchSelectObjectType[normalizedModelName];
	}

	get objectText(): string {
		return this.modelTitle || capitalize(this.modelName);
	}

	get confirmWord(): string | undefined {
		return 'DELETE';
	}

	batchCreate(items: ModelType[], options?: BatchSelectActionOptions) {
		return this.batchAction(BatchSelectActionType.CREATE, undefined, items, options);
	}

	batchUpdate(items: ModelType[]) {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.UPDATE, objectIds);
	}

	batchFavorite(items: ModelType[], options?: BatchSelectActionOptions) {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.FAVORITE, objectIds, undefined, options);
	}

	batchUnfavorite(items: ModelType[]) {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.UNFAVORITE, objectIds);
	}

	batchUpdateTags(items: ModelType[]): Promise<void> {
		return new Promise((resolve, reject) => {
			let wasApplied = false;

			const popup = this.mmTagsService.openBatchTagsContext((selectedTags) => {
				wasApplied = true;

				const objectIds = items.map((item) => item.numericId!);
				this.batchAction(BatchSelectActionType.UPDATE_TAGS, objectIds, selectedTags)
					.then(resolve)
					.catch(reject);
			});

			if (popup) {
				popup?.on(PopupEvent.CLOSING, this, () => {
					if (!wasApplied) {
						reject();
					}
				});
			} else {
				reject();
			}
		});
	}

	batchDelete(items: ModelType[]): Promise<void> {
		const firstItem = items[0],
			// only some models (boards, reports, loops) have soft delete
			isPermanentlyDelete = !!firstItem && 'deleted' in firstItem && !!firstItem.deleted,
			modelName = this.modelName;

		let finalCustomText: string | undefined;
		if (isPermanentlyDelete) {
			finalCustomText = '<strong>This cannot be undone.</strong>';
		} else if (
			modelName === ModelName.BOARD ||
			modelName === ModelName.BOARD_LOOP ||
			modelName === ModelName.REPORT
		) {
			finalCustomText = `<strong>(${items.length}) ${this.objectText}s will be moved to Recently deleted and deleted permanently after 30 days.</strong>`;

			if (modelName === ModelName.BOARD) {
				finalCustomText +=
					'<br><br>Note that this dashboard will be removed from all loops, reports and home widgets, and all related notifications will be deleted. These will not be restored even if you restore the dashboard.<br><br>';
			}

			if (modelName === ModelName.REPORT) {
				finalCustomText +=
					'<br><br>Note that this report will be removed from all Home widgets, and all related notifications will be deleted. These will not be restored even if you restore the report.<br><br>';
			}
		}

		return new Promise((resolve, reject) => {
			let wasSubmitted = false;
			const affirmativeFunc = () => {
				wasSubmitted = true;
				this.batchDeleteAction(items).then(resolve).catch(reject);
			};

			const popup = this.openBatchDeleteConfirmationPopup(
				affirmativeFunc,
				items.length,
				finalCustomText,
			);

			popup.on(PopupEvent.CLOSING, this, () => {
				if (!wasSubmitted) {
					reject();
				}
			});
		});
	}

	batchUpdateTagsAction(
		items: ModelType[],
		selectedTags: number[],
		options?: BatchSelectActionOptions,
	): Promise<void> {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(
			BatchSelectActionType.UPDATE_TAGS,
			objectIds,
			selectedTags,
			options,
		);
	}

	batchDeleteAction(items: ModelType[], options?: BatchSelectActionOptions): Promise<void> {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.DELETE, objectIds, undefined, options);
	}

	batchRestoreAction(items: ModelType[]): Promise<void> {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.RESTORE, objectIds);
	}

	openBatchDeleteConfirmationPopup(
		affirmativeFunc: () => void,
		itemCount: number,
		customText?: string,
		objectText = this.objectText,
	) {
		let title = 'Are you sure you want to delete ';
		if (itemCount === 1) {
			title += `this ${objectText}`;
		} else {
			title += `these ${itemCount} ${objectText}s`;
		}
		title += '?';

		return this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title,
			text: customText || '<strong>This cannot be undone.</strong>',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
			confirmWord: this.confirmWord,
		});
	}

	protected batchAction(
		operationType: BatchSelectActionType,
		objectIds?: number[],
		data?: unknown[] | object, // a list of items, or the operation's options (e.g. tags { add, remove })
		options?: BatchSelectActionOptions,
	) {
		if (objectIds?.length === 0 || (Array.isArray(data) && data.length === 0)) {
			return Promise.resolve();
		}

		let actionText = 'Process';
		if (
			operationType === BatchSelectActionType.CREATE ||
			operationType === BatchSelectActionType.ADD_TO_REPORT
		) {
			actionText = 'Add';
		} else if (
			operationType === BatchSelectActionType.UPDATE ||
			operationType === BatchSelectActionType.UPDATE_TAGS
		) {
			actionText = 'Updat';
		} else if (operationType === BatchSelectActionType.FAVORITE) {
			actionText = 'Favorit';
		} else if (operationType === BatchSelectActionType.UNFAVORITE) {
			actionText = 'Unfavorit';
		} else if (operationType === BatchSelectActionType.DELETE) {
			actionText = 'Delet';
		} else if (operationType === BatchSelectActionType.RESTORE) {
			actionText = 'Restor';
		} else if (
			operationType === BatchSelectActionType.PURGE_DATA ||
			operationType === BatchSelectActionType.PURGE_DATA_FOR_METRIC_SETUPS
		) {
			actionText = 'Purg';
		}

		const objectType = options?.objectType ?? this.objectType,
			objectText = options?.objectText ?? this.objectText,
			numberOfItems = objectIds?.length || (Array.isArray(data) ? data.length : 0) || 0,
			payload = {
				objectType,
				operationType,
				objectIds,
				data,
			};

		let infolayer: InfolayerItemGeneral | null = null;
		if (!options?.hideInfolayer) {
			infolayer = this.infolayerService.loading(`${actionText}ing ${objectText}s...`);
		}

		let doneItemsCount = 0;
		return this.requestService
			.apiWithAsyncReply('POST', '/batch', payload, undefined, () => {
				doneItemsCount++;

				if (infolayer) {
					infolayer.text = `${actionText}ing ${objectText}s... (${doneItemsCount}/${numberOfItems})`;
				}
				return doneItemsCount === numberOfItems;
			})
			.then(() => {
				if (infolayer) {
					infolayer.ok(`All ${objectText}s ${actionText.toLowerCase()}ed!`);
				}
			})
			.catch((error) => {
				if (infolayer) {
					infolayer.close();
				}
				throw error;
			});
	}

	// ------ CACHING HELPER ------

	updateModelCache(
		cached: ModelType[],
		freshRecords: RecordType[],
		createModelFn?: updateModelCacheHelperCreateFn<RecordType, ModelType>,
	): boolean {
		const createFn =
			createModelFn ||
			((record: RecordType) => {
				return this.createModelFromRecord(record);
			});
		return ModelManagerForStoreService.updateModelCacheHelper<RecordType, ModelType>(
			cached,
			freshRecords,
			createFn,
		);
	}

	static updateModelCacheHelper<T1, T2 extends { model: unknown }>(
		cached: T2[],
		freshRecords: T1[],
		createModelFn: updateModelCacheHelperCreateFn<T1, T2>,
	): boolean {
		// works also with unsaved records (does not look at IDs)
		const cachedRecords = cached.map((c) => c.model as T1), // T1, or a union with it
			removeRecords = cachedRecords.filter((record: T1) => !freshRecords.includes(record)),
			addRecords = freshRecords.filter((record: T1) => !cachedRecords.includes(record));

		const allRecords = uniq([...cachedRecords, ...freshRecords]);

		let isChanged = false;

		allRecords.forEach((record: T1) => {
			if (removeRecords.includes(record)) {
				const model = cached.find((c) => c.model === record);
				if (model) {
					const indexToRemove = cached.indexOf(model);
					cached.splice(indexToRemove, 1);
					isChanged = true;
				}
			} else if (addRecords.includes(record)) {
				if (record) {
					cached.push(createModelFn(record));
					isChanged = true;
				}
			}
		});

		return isChanged;
	}

	// ------ GLOBAL VISUAL SETTINGS ------

	useGlobalVisualSettings<P extends GlobalVisualSettingsProperties>(
		input: P,
		globalVisualSettings: APIData.GlobalVisualSettings.VisualSettings,
	): P {
		const properties: GlobalVisualSettingsProperties = input; // the same object, written in place

		const color = globalVisualSettings.color;
		if (typeof properties.color === 'undefined' && color) {
			properties.color = color;
		}

		const themeId = globalVisualSettings.themeId;
		if (typeof properties.themeId === 'undefined' && themeId) {
			properties.themeId = themeId;
		}

		const bg = globalVisualSettings.settings?.bg;
		if (typeof properties.bg === 'undefined' && bg) {
			properties.bg = bg;
		}

		const bgOpacity = globalVisualSettings.settings?.bgOpacity;
		if (typeof properties.bgOpacity === 'undefined' && bgOpacity) {
			properties.bgOpacity = bgOpacity;
		}

		const bgOriginal = globalVisualSettings.settings?.bgOriginal;
		if (typeof properties.bgOriginal === 'undefined' && bgOriginal) {
			properties.bgOriginal = bgOriginal;
		}

		const bgCrop = globalVisualSettings.settings?.bgCrop;
		if (typeof properties.bgCrop === 'undefined' && bgCrop) {
			properties.bgCrop = bgCrop;
		}

		const logo = globalVisualSettings.settings?.logo;
		if (typeof properties.logo === 'undefined' && logo) {
			properties.logo = logo;
		}

		const logoOriginal = globalVisualSettings.settings?.logoOriginal;
		if (typeof properties.logoOriginal === 'undefined' && logoOriginal) {
			properties.logoOriginal = logoOriginal;
		}

		const logoCrop = globalVisualSettings.settings?.logoCrop;
		if (typeof properties.logoCrop === 'undefined' && logoCrop) {
			properties.logoCrop = logoCrop;
		}

		return input;
	}
}
